import React from 'react';

interface RenderMetaProps {
  title?: string;
  data: Record<string, any>;
}

const RenderMeta: React.FC<RenderMetaProps> = ({ title, data }) => {
  // Helper function to format keys by replacing underscores with spaces and capitalizing words
  const formatKey = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Recursive function to render values based on their type
  const renderValue = (value: any): React.ReactNode => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Render nested objects as another table
      return (
        <table className="min-w-full border mt-2 bg-gray-50">
          <tbody>
            {Object.entries(value).map(([nestedKey, nestedValue]) => (
              <tr key={nestedKey} className="border-b">
                <td className="px-4 py-2 font-medium text-gray-700">{formatKey(nestedKey)}</td>
                <td className="px-4 py-2">{renderValue(nestedValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else if (Array.isArray(value)) {
      // Render arrays with list items, handling nested arrays
      return (
        <ul className="list-disc pl-6 space-y-1">
          {value.map((item, index) => (
            <li key={index} className="text-gray-600">
              {renderValue(item)}
            </li>
          ))}
        </ul>
      );
    } else {
      // Render primitive values directly
      return <span className="text-gray-800">{value}</span>;
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      {title && <h3 className="text-xl font-semibold mb-4 text-gray-900">{title}</h3>}
      <table className="min-w-full text-left text-sm border">
        <tbody>
          {Object.entries(data).map(([key, value]) => (
            <tr key={key} className="border-b">
              <td className="px-4 py-2 font-semibold text-gray-700">{formatKey(key)}</td>
              <td className="px-4 py-2">{renderValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RenderMeta;
